/**
 * features/audit/components/CalibrationView.tsx — MER Algo Footprints v7.1H
 *
 * Surfaces CalibrationReport from the calibration engine.
 * Read-only display — no engine logic here, no scoring changes.
 * Requires ≥ 10 completed audit records with outcomes before meaningful output.
 */

import React, { useMemo } from 'react';
import { AuditRecord }        from '../../../types';
import { runCalibrationEngine } from '../../../domains/journal/calibration/engine';
import type {
  CalibrationReport,
  SetupFamilyMetrics,
  ReadinessBandMetrics,
  CalibrationCandidate,
  PenaltyEffectivenessMetrics,
} from '../../../domains/journal/calibration/types';

// ─── Primitives ───────────────────────────────────────────────────────────────

const MONO: React.CSSProperties = { fontFamily: 'var(--mono)' };

function Lbl({ children }: { children: string }) {
  return (
    <span style={{ fontSize: 9, color: 'var(--blue-dim)', ...MONO, letterSpacing: .5 }}>
      {children}
    </span>
  );
}

function Val({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: color ?? 'var(--text)', ...MONO }}>
      {children}
    </span>
  );
}

function SectionHead({ children }: { children: string }) {
  return (
    <div style={{
      fontSize: 9, fontWeight: 700, color: 'var(--gold)', ...MONO,
      letterSpacing: 1.2, borderBottom: '1px solid var(--border)',
      paddingBottom: 4, marginBottom: 8, marginTop: 14,
    }}>
      {children}
    </div>
  );
}

function pct(n: number | null): string {
  if (n === null) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function r(n: number | null): string {
  if (n === null) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}R`;
}

function rColor(n: number | null): string {
  if (n === null) return 'var(--blue-dim)';
  return n > 0 ? 'var(--green)' : n < 0 ? '#f87171' : 'var(--blue-muted)';
}

// ─── Overview cards ───────────────────────────────────────────────────────────

function OverviewCards({ report }: { report: CalibrationReport }) {
  const cards = [
    { label: 'Win Rate',      value: pct(report.overall_win_rate),    color: rColor(report.overall_win_rate ? report.overall_win_rate - 0.5 : null) },
    { label: 'Avg R',         value: r(report.overall_avg_r),          color: rColor(report.overall_avg_r) },
    { label: 'Expectancy',    value: r(report.overall_expectancy),      color: rColor(report.overall_expectancy) },
    { label: 'False Pos',     value: pct(report.overall_false_pos_rate), color: report.overall_false_pos_rate && report.overall_false_pos_rate > 0.35 ? '#f87171' : 'var(--text)' },
    { label: 'Missed Wins',   value: String(report.missed_winners),     color: report.missed_winners > 3 ? 'var(--gold)' : 'var(--text)' },
    { label: 'Avoided Losses',value: String(report.avoided_losses),     color: 'var(--green)' },
    { label: 'Records',       value: String(report.record_count),       color: 'var(--text)' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(100px,1fr))', gap: 6, marginBottom: 10 }}>
      {cards.map(c => (
        <div key={c.label} style={{
          background: 'var(--surface2)', border: '1px solid var(--border)',
          borderRadius: 4, padding: '8px 10px',
        }}>
          <Lbl>{c.label}</Lbl>
          <div style={{ marginTop: 4 }}><Val color={c.color}>{c.value}</Val></div>
        </div>
      ))}
    </div>
  );
}

// ─── Setup family table ───────────────────────────────────────────────────────

function SetupFamilyTable({ rows }: { rows: SetupFamilyMetrics[] }) {
  if (!rows.length) return <Empty text="No setup data yet" />;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            {['Setup', 'N', 'Win%', 'Avg R', 'Expectancy', 'FP%'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '3px 6px', ...MONO,
                color: 'var(--blue-dim)', fontWeight: 700, fontSize: 9, letterSpacing: .5 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.setup_type} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '4px 6px', ...MONO, color: 'var(--text)', fontSize: 10 }}>
                {row.setup_type.replace(/_/g, ' ')}
              </td>
              <td style={{ padding: '4px 6px', ...MONO, color: 'var(--blue-muted)' }}>{row.count}</td>
              <td style={{ padding: '4px 6px', ...MONO, color: rColor(row.win_rate ? row.win_rate - 0.5 : null) }}>{pct(row.win_rate)}</td>
              <td style={{ padding: '4px 6px', ...MONO, color: rColor(row.avg_r) }}>{r(row.avg_r)}</td>
              <td style={{ padding: '4px 6px', ...MONO, color: rColor(row.expectancy) }}>{r(row.expectancy)}</td>
              <td style={{ padding: '4px 6px', ...MONO, color: row.false_pos_rate && row.false_pos_rate > 0.4 ? '#f87171' : 'var(--text)' }}>{pct(row.false_pos_rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Readiness band table ─────────────────────────────────────────────────────

function ReadinessBandTable({ rows }: { rows: ReadinessBandMetrics[] }) {
  if (!rows.length) return <Empty text="No readiness data yet" />;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            {['Band', 'N', 'Win%', 'Avg R', 'Expectancy', 'Gap'].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '3px 6px', ...MONO,
                color: 'var(--blue-dim)', fontWeight: 700, fontSize: 9 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.band} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '4px 6px', ...MONO, color: 'var(--text)' }}>{row.band}</td>
              <td style={{ padding: '4px 6px', ...MONO, color: 'var(--blue-muted)' }}>{row.count}</td>
              <td style={{ padding: '4px 6px', ...MONO, color: rColor(row.win_rate ? row.win_rate - 0.5 : null) }}>{pct(row.win_rate)}</td>
              <td style={{ padding: '4px 6px', ...MONO, color: rColor(row.avg_r) }}>{r(row.avg_r)}</td>
              <td style={{ padding: '4px 6px', ...MONO, color: rColor(row.expectancy) }}>{r(row.expectancy)}</td>
              <td style={{ padding: '4px 6px', ...MONO, color: row.calibration_gap && Math.abs(row.calibration_gap) > 0.15 ? 'var(--gold)' : 'var(--blue-muted)' }}>
                {row.calibration_gap !== null ? `${row.calibration_gap >= 0 ? '+' : ''}${(row.calibration_gap * 100).toFixed(1)}%` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Calibration candidates ───────────────────────────────────────────────────

const KIND_COLOR: Record<string, string> = {
  THRESHOLD_MISS_LOWER:      'var(--gold)',
  THRESHOLD_MISS_UPPER:      '#f87171',
  READINESS_MISS_LOWER:      'var(--gold)',
  FALSE_POSITIVE_PATTERN:    '#f87171',
  FALSE_NEGATIVE_PATTERN:    'var(--green)',
  OVER_FILTER:               'var(--green)',
  UNDER_FILTER:              '#f87171',
  OVER_FILTER_CANDIDATE:     'var(--green)',
  UNDER_FILTER_CANDIDATE:    '#f87171',
  PENALTY_WEIGHT_REVIEW_CANDIDATE: 'var(--purple)',
};

function CandidateCard({ c }: { c: CalibrationCandidate }) {
  const color = KIND_COLOR[c.kind] ?? 'var(--blue-muted)';
  return (
    <div style={{
      background: 'var(--surface2)', border: `1px solid ${color}40`,
      borderLeft: `3px solid ${color}`, borderRadius: 4,
      padding: '8px 10px', marginBottom: 6,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontSize: 9, fontWeight: 700, color, ...MONO, letterSpacing: .8 }}>
          {c.kind.replace(/_/g, ' ')}
        </span>
        <span style={{ fontSize: 9, color: 'var(--blue-dim)', ...MONO }}>{c.symbol}</span>
      </div>
      <div style={{ fontSize: 10, color: 'var(--text)', lineHeight: 1.5, marginBottom: 4 }}>{c.reason}</div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 9, color: 'var(--blue-dim)', ...MONO }}>
          Value: <span style={{ color: 'var(--text)' }}>{c.current_value.toFixed(1)}</span>
          {' / '}Threshold: <span style={{ color: 'var(--text)' }}>{c.threshold}</span>
          {' / '}Δ: <span style={{ color: 'var(--gold)' }}>{c.delta.toFixed(1)}</span>
        </span>
        {c.realized_r !== null && (
          <span style={{ fontSize: 9, ...MONO, color: rColor(c.realized_r) }}>
            Outcome: {r(c.realized_r)}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Penalty effectiveness ────────────────────────────────────────────────────

function PenaltyTable({ rows }: { rows: PenaltyEffectivenessMetrics[] }) {
  if (!rows.length) return <Empty text="No penalty data yet" />;
  const EFF_COLOR: Record<string, string> = {
    effective: 'var(--green)', ineffective: '#f87171',
    neutral: 'var(--blue-muted)', insufficient_data: 'var(--blue-dim)',
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map(row => (
        <div key={row.penalty_type} style={{
          background: 'var(--surface2)', border: '1px solid var(--border)',
          borderRadius: 4, padding: '8px 10px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: 'var(--text)', ...MONO }}>{row.penalty_type}</span>
            <span style={{ fontSize: 9, fontWeight: 700, color: EFF_COLOR[row.effectiveness], ...MONO, letterSpacing: .5 }}>
              {row.effectiveness.toUpperCase().replace(/_/g, ' ')}
            </span>
          </div>
          <div style={{ fontSize: 9, color: 'var(--blue-dim)', lineHeight: 1.4 }}>{row.reason}</div>
          <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 9, ...MONO, color: 'var(--blue-dim)' }}>
              N={row.count} · WR (penalized): <span style={{ color: 'var(--text)' }}>{pct(row.win_rate_when_penalized)}</span>
              {' vs '}<span style={{ color: 'var(--text)' }}>{pct(row.win_rate_without_penalty)}</span>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function Empty({ text }: { text: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--blue-dim)', ...MONO, fontSize: 10 }}>
      {text}
    </div>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

const MIN_RECORDS = 10;

export function CalibrationView({ auditRecords }: { auditRecords: AuditRecord[] }) {
  const report = useMemo<CalibrationReport | null>(() => {
    const completed = auditRecords.filter(r => r.status !== 'open');
    if (completed.length < MIN_RECORDS) return null;
    try { return runCalibrationEngine(auditRecords); }
    catch { return null; }
  }, [auditRecords]);

  const completed = auditRecords.filter(r => r.status !== 'open').length;

  if (!report) {
    return (
      <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--blue-dim)', ...MONO, fontSize: 11 }}>
        <div style={{ fontSize: 24, opacity: .2, marginBottom: 12 }}>📊</div>
        <div style={{ marginBottom: 6 }}>
          Calibration requires {MIN_RECORDS} completed trade records.
        </div>
        <div style={{ fontSize: 10, color: '#334155' }}>
          {completed} / {MIN_RECORDS} completed records
          {completed > 0 && ` — ${MIN_RECORDS - completed} more needed`}
        </div>
        <div style={{ marginTop: 12, fontSize: 9, color: '#334155', maxWidth: 340, margin: '12px auto 0' }}>
          Close out audit records with outcomes (win / loss / breakeven) to populate this view.
        </div>
      </div>
    );
  }

  const candidates = report.calibration_candidates;
  const dateRange = report.date_range
    ? `${new Date(report.date_range.from).toLocaleDateString()} – ${new Date(report.date_range.to).toLocaleDateString()}`
    : null;

  return (
    <div style={{ fontSize: 11 }}>

      {/* Report header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--gold)', ...MONO, letterSpacing: 1.2 }}>
          CALIBRATION REPORT
        </div>
        <div style={{ fontSize: 9, color: 'var(--blue-dim)', ...MONO }}>
          {dateRange ?? `${report.record_count} records`}
        </div>
      </div>

      {/* Overview */}
      <OverviewCards report={report} />

      {/* Calibration candidates — most actionable, top of page */}
      {candidates.length > 0 && (
        <>
          <SectionHead>{`CALIBRATION SIGNALS (${candidates.length})`}</SectionHead>
          <div style={{ fontSize: 9, color: 'var(--blue-dim)', ...MONO, marginBottom: 8, lineHeight: 1.5 }}>
            These are threshold-level findings from your trade history. Review — do not auto-adjust.
          </div>
          {candidates.slice(0, 8).map((c, i) => <CandidateCard key={i} c={c} />)}
        </>
      )}

      {/* Setup family breakdown */}
      <SectionHead>SETUP FAMILY BREAKDOWN</SectionHead>
      <SetupFamilyTable rows={report.by_setup_family} />

      {/* Readiness bands */}
      <SectionHead>READINESS BANDS</SectionHead>
      <ReadinessBandTable rows={report.by_readiness_band} />

      {/* Penalty effectiveness */}
      {report.penalty_metrics.length > 0 && (
        <>
          <SectionHead>PENALTY EFFECTIVENESS</SectionHead>
          <PenaltyTable rows={report.penalty_metrics} />
        </>
      )}

    </div>
  );
}
